// src/features/monetize/useViewerLedger.ts

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { ApiError } from '@/shared/api/ApiError';
import { MONETIZE_QUERY_KEYS } from '@/lib/constants/monetize';
import { MonetizeApi } from './MonetizeApi';
import { useMonetizeEligibility } from './useMonetizeEligibility';

/** The operator's viewer behind an e-mail address; `notFound` when nobody signed in with it. */
export function useViewerLookup(email: string) {
  const { selectedCId } = useAuth();
  const { eligible } = useMonetizeEligibility();
  const norm = email.trim().toLowerCase();
  const query = useQuery({
    queryKey: MONETIZE_QUERY_KEYS.viewer(selectedCId ?? 0, norm),
    queryFn: () => MonetizeApi.findViewer(selectedCId!, norm),
    enabled: eligible && selectedCId != null && norm.length > 0,
    retry: false,
    staleTime: 30 * 1000,
  });
  const notFound = query.isError && query.error instanceof ApiError && query.error.httpStatusCode === 404;
  return { viewer: query.data, notFound, isLoading: query.isLoading, isError: query.isError && !notFound, error: query.error };
}

/** Everything about one viewer in one read: access rows, purchases, subscriptions, history. */
export function useViewerLedger(viewerId: string | null) {
  const { selectedCId } = useAuth();
  const query = useQuery({
    queryKey: MONETIZE_QUERY_KEYS.ledger(selectedCId ?? 0, viewerId ?? ''),
    queryFn: () => MonetizeApi.getViewerLedger(selectedCId!, viewerId!),
    enabled: selectedCId != null && !!viewerId,
    staleTime: 15 * 1000,
  });
  return { ledger: query.data, isLoading: query.isLoading, isError: query.isError, error: query.error, refetch: query.refetch };
}
